/**
 * FolderTreePanel — left pane of the file manager.
 *
 * Shows "All Assets" at top, then recursive folder tree,
 * then a "Collections" section with saved filter views.
 * Provides context menu for folder CRUD and a "+" button.
 */

import { useState } from 'react'
import {
  FolderOpen, Plus, FolderPlus, Edit3, Trash2, Move,
  Bookmark, Star, Tag, Film, Image, Music, FileText, Folder, Zap, Heart, Eye,
} from 'lucide-react'
import toast from 'react-hot-toast'
import type { FolderNode, Collection } from '../../types/fileManager'
import FolderTreeNode from './FolderTreeNode'
import ContextMenu, { MenuItem } from './ContextMenu'
import CreateFolderModal from './CreateFolderModal'
import MoveFolderModal from './MoveFolderModal'
import { assetFolderApi } from '../../services/assetFolderApi'
import { collectionApi } from '../../services/assetFolderApi'
import { apiErrorDetail } from '../../utils/dataValidator'

const ICON_MAP: Record<string, React.ReactNode> = {
  bookmark: <Bookmark className="w-3.5 h-3.5" />,
  star: <Star className="w-3.5 h-3.5" />,
  tag: <Tag className="w-3.5 h-3.5" />,
  film: <Film className="w-3.5 h-3.5" />,
  image: <Image className="w-3.5 h-3.5" />,
  music: <Music className="w-3.5 h-3.5" />,
  'file-text': <FileText className="w-3.5 h-3.5" />,
  folder: <Folder className="w-3.5 h-3.5" />,
  zap: <Zap className="w-3.5 h-3.5" />,
  heart: <Heart className="w-3.5 h-3.5" />,
  eye: <Eye className="w-3.5 h-3.5" />,
}

const COLOR_MAP: Record<string, string> = {
  red: 'text-red-400',
  orange: 'text-orange-400',
  yellow: 'text-yellow-400',
  green: 'text-emerald-400',
  blue: 'text-blue-400',
  purple: 'text-purple-400',
}

interface FolderTreePanelProps {
  projectId: string
  tree: FolderNode[]
  rootFolderId?: number | null
  selectedFolderId: number | null
  onSelectFolder: (id: number | null) => void
  onTreeChange: () => void
  /** DnD handlers forwarded to tree nodes */
  onDragOver?: (e: React.DragEvent, node: FolderNode) => void
  onDragLeave?: (e: React.DragEvent) => void
  onDrop?: (e: React.DragEvent, node: FolderNode) => void
  /** Collections */
  collections?: Collection[]
  activeCollectionId?: number | null
  onSelectCollection?: (id: number | null) => void
  onCollectionsChange?: () => void
}

export default function FolderTreePanel({
  projectId,
  tree,
  rootFolderId,
  selectedFolderId,
  onSelectFolder,
  onTreeChange,
  onDragOver,
  onDragLeave,
  onDrop,
  collections = [],
  activeCollectionId,
  onSelectCollection,
  onCollectionsChange,
}: FolderTreePanelProps) {
  const [ctxMenu, setCtxMenu] = useState<{ x: number; y: number; node: FolderNode } | null>(null)
  const [collCtxMenu, setCollCtxMenu] = useState<{ x: number; y: number; collection: Collection } | null>(null)
  const [renamingId, setRenamingId] = useState<number | null>(null)
  const [showCreateModal, setShowCreateModal] = useState<FolderNode | null>(null)
  const [showMoveModal, setShowMoveModal] = useState<FolderNode | null>(null)

  const handleContextMenu = (e: React.MouseEvent, node: FolderNode) => {
    setCtxMenu({ x: e.clientX, y: e.clientY, node })
  }

  const handleRenameSave = async (id: number, newName: string) => {
    setRenamingId(null)
    // Find the current name — if unchanged, skip
    const findNode = (nodes: FolderNode[]): FolderNode | null => {
      for (const n of nodes) {
        if (n.id === id) return n
        const found = findNode(n.children || [])
        if (found) return found
      }
      return null
    }
    const node = findNode(tree)
    if (!node || newName === node.name) return

    try {
      await assetFolderApi.renameFolder(id, newName)
      toast.success('Folder renamed')
      onTreeChange()
    } catch (err) {
      toast.error(apiErrorDetail(err, 'Failed to rename folder'))
    }
  }

  const handleDeleteFolder = async (node: FolderNode) => {
    const hasAssets = node.asset_count > 0
    const hasChildren = node.children && node.children.length > 0
    if (hasAssets || hasChildren) {
      if (!confirm(`Delete "${node.name}" and all its contents? This cannot be undone.`)) return
    }
    try {
      await assetFolderApi.deleteFolder(node.id, true)
      toast.success('Folder deleted')
      if (selectedFolderId === node.id) onSelectFolder(null)
      onTreeChange()
    } catch (err) {
      toast.error(apiErrorDetail(err, 'Failed to delete folder'))
    }
  }

  const handleCreateFolder = async (name: string, folderType: string, createDefaultChildren: boolean = false) => {
    if (!showCreateModal) return
    try {
      await assetFolderApi.createFolder(projectId, name, showCreateModal.id, folderType, createDefaultChildren)
      toast.success('Folder created')
      onTreeChange()
    } catch (err) {
      toast.error(apiErrorDetail(err, 'Failed to create folder'))
      throw err
    }
  }

  const handleMoveFolder = async (targetId: number) => {
    if (!showMoveModal) return
    try {
      await assetFolderApi.moveFolder(showMoveModal.id, targetId)
      toast.success('Folder moved')
      onTreeChange()
    } catch (err) {
      toast.error(apiErrorDetail(err, 'Failed to move folder'))
      throw err
    }
  }

  const handleDeleteCollection = async (collection: Collection) => {
    if (!confirm(`Delete collection "${collection.name}"?`)) return
    try {
      await collectionApi.delete(collection.id)
      toast.success('Collection deleted')
      if (activeCollectionId === collection.id) {
        onSelectCollection?.(null)
      }
      onCollectionsChange?.()
    } catch (err) {
      toast.error(apiErrorDetail(err, 'Failed to delete collection'))
    }
  }

  // Build context menu items
  const ctxItems: MenuItem[] = ctxMenu ? [
    {
      label: 'New Subfolder',
      icon: <FolderPlus className="w-4 h-4" />,
      onClick: () => setShowCreateModal(ctxMenu.node),
    },
    ...(ctxMenu.node.folder_type !== 'project_root' ? [
      {
        label: 'Rename',
        icon: <Edit3 className="w-4 h-4" />,
        onClick: () => setRenamingId(ctxMenu.node.id),
      },
      {
        label: 'Move',
        icon: <Move className="w-4 h-4" />,
        onClick: () => setShowMoveModal(ctxMenu.node),
      },
      {
        label: 'Delete',
        icon: <Trash2 className="w-4 h-4" />,
        danger: true,
        onClick: () => handleDeleteFolder(ctxMenu.node),
      },
    ] : []),
  ] : []

  // Build collection context menu items
  const collCtxItems: MenuItem[] = collCtxMenu ? [
    {
      label: 'Delete',
      icon: <Trash2 className="w-4 h-4" />,
      danger: true,
      onClick: () => handleDeleteCollection(collCtxMenu.collection),
    },
  ] : []

  // Build a synthetic root node for the "+" button using rootFolderId from backend
  const rootFolder: FolderNode | null = rootFolderId
    ? {
        id: rootFolderId,
        project_id: projectId,
        parent_id: null,
        name: 'Assets',
        slug: 'assets',
        folder_type: 'project_root',
        path_cache: 'Assets',
        sort_order: 0,
        metadata: null,
        children: tree,
        asset_count: 0,
      }
    : (tree.length > 0 ? tree[0] : null)

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-3 border-b border-white/10">
        <span className="text-xs font-semibold uppercase tracking-wider text-[var(--rq-text-soft)]">Folders</span>
        {rootFolder && (
          <button
            onClick={() => setShowCreateModal(rootFolder)}
            className="p-1 rounded hover:bg-white/10 text-[var(--rq-text-soft)] hover:text-[var(--rq-text)] transition-colors"
            title="New folder"
          >
            <Plus className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* All Assets option */}
      <div
        className={`fm-tree-node ${selectedFolderId === null && !activeCollectionId ? 'fm-tree-node-selected' : ''}`}
        style={{ paddingLeft: '8px' }}
        onClick={() => { onSelectFolder(null); onSelectCollection?.(null) }}
      >
        <FolderOpen className="w-4 h-4 flex-shrink-0 text-[var(--rq-text-soft)]" />
        <span className="flex-1 text-xs font-medium">All Assets</span>
      </div>

      {/* Tree */}
      <div className="flex-1 overflow-y-auto py-1">
        {tree.map(root => (
          <FolderTreeNode
            key={root.id}
            node={root}
            depth={0}
            selectedId={activeCollectionId ? null : selectedFolderId}
            onSelect={(id) => { onSelectFolder(id); onSelectCollection?.(null) }}
            onContextMenu={handleContextMenu}
            onRenameSave={handleRenameSave}
            renamingId={renamingId}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
          />
        ))}

        {/* Collections section */}
        {collections.length > 0 && (
          <>
            <div className="flex items-center px-3 pt-4 pb-1">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-[var(--rq-text-soft)]">Collections</span>
            </div>
            {collections.map(coll => {
              const iconNode = ICON_MAP[coll.icon || 'bookmark'] || <Bookmark className="w-3.5 h-3.5" />
              const colorClass = COLOR_MAP[coll.color || 'blue'] || 'text-[var(--rq-text-soft)]'

              return (
                <div
                  key={coll.id}
                  className={`fm-collection-item fm-tree-node ${activeCollectionId === coll.id ? 'fm-tree-node-selected' : ''}`}
                  style={{ paddingLeft: '8px' }}
                  onClick={() => { onSelectCollection?.(coll.id); onSelectFolder(null) }}
                  onContextMenu={e => { e.preventDefault(); setCollCtxMenu({ x: e.clientX, y: e.clientY, collection: coll }) }}
                >
                  <span className={`flex-shrink-0 ${colorClass}`}>{iconNode}</span>
                  <span className="flex-1 text-xs font-medium truncate">{coll.name}</span>
                </div>
              )
            })}
          </>
        )}
      </div>

      {/* Folder context menu */}
      {ctxMenu && (
        <ContextMenu
          x={ctxMenu.x}
          y={ctxMenu.y}
          items={ctxItems}
          onClose={() => setCtxMenu(null)}
        />
      )}

      {/* Collection context menu */}
      {collCtxMenu && (
        <ContextMenu
          x={collCtxMenu.x}
          y={collCtxMenu.y}
          items={collCtxItems}
          onClose={() => setCollCtxMenu(null)}
        />
      )}

      {/* Create folder modal */}
      {showCreateModal && (
        <CreateFolderModal
          parentName={showCreateModal.name}
          onClose={() => setShowCreateModal(null)}
          onCreate={handleCreateFolder}
        />
      )}

      {/* Move folder modal */}
      {showMoveModal && (
        <MoveFolderModal
          title={`Move "${showMoveModal.name}"`}
          tree={tree}
          excludeIds={new Set([showMoveModal.id])}
          onClose={() => setShowMoveModal(null)}
          onSelect={handleMoveFolder}
        />
      )}
    </div>
  )
}
